import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DeviceCategoryFiltering = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPayer, setSelectedPayer] = useState('all');
  const [selectedImpact, setSelectedImpact] = useState('all');

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'cardiovascular', label: 'Cardiovascular Devices' },
    { value: 'orthopedic', label: 'Orthopedic Implants' },
    { value: 'surgical', label: 'Surgical Instruments' },
    { value: 'diagnostic', label: 'Diagnostic Equipment' },
    { value: 'neurology', label: 'Neurological Devices' }
  ];

  const payerOptions = [
    { value: 'all', label: 'All Payers' },
    { value: 'medicare', label: 'Medicare/CMS' },
    { value: 'bcbs', label: 'Blue Cross Blue Shield' },
    { value: 'aetna', label: 'Aetna' },
    { value: 'uhc', label: 'United Healthcare' },
    { value: 'cigna', label: 'Cigna' }
  ];

  const impactOptions = [
    { value: 'all', label: 'All Impact Levels' },
    { value: 'critical', label: 'Critical Impact' },
    { value: 'high', label: 'High Impact' },
    { value: 'medium', label: 'Medium Impact' },
    { value: 'low', label: 'Low Impact' }
  ];

  const policies = [
    {
      id: 1,
      title: "Cardiac Stent Coverage Criteria Update",
      category: "cardiovascular",
      payer: "medicare",
      impact: "high",
      effectiveDate: "2026-03-01",
      description: "Updated medical necessity criteria for drug-eluting stents in high-risk cardiac patients with new documentation requirements.",
      affectedProducts: 12,
      implementationDeadline: "2026-02-15"
    },
    {
      id: 2,
      title: "Total Hip Arthroplasty Policy Revision",
      category: "orthopedic",
      payer: "bcbs",
      impact: "medium",
      effectiveDate: "2026-02-15",
      description: "Modified age restrictions and medical necessity criteria for total hip replacement procedures across multiple patient populations.",
      affectedProducts: 8,
      implementationDeadline: "2026-02-01"
    },
    {
      id: 3,
      title: "Robotic Surgery Prior Authorization",
      category: "surgical",
      payer: "aetna",
      impact: "critical",
      effectiveDate: "2026-02-01",
      description: "New mandatory prior authorization requirements for all robotic-assisted surgical procedures with 30-day implementation period.",
      affectedProducts: 15,
      implementationDeadline: "2026-01-31"
    },
    {
      id: 4,
      title: "Advanced Imaging Reimbursement Expansion",
      category: "diagnostic",
      payer: "uhc",
      impact: "low",
      effectiveDate: "2026-04-01",
      description: "Coverage expansion for advanced diagnostic imaging devices including new CPT codes and updated reimbursement rate schedules.",
      affectedProducts: 6,
      implementationDeadline: "2026-03-15"
    },
    {
      id: 5,
      title: "Cardiac Monitoring Device Rate Update",
      category: "cardiovascular",
      payer: "cigna",
      impact: "medium",
      effectiveDate: "2026-02-01",
      description: "Quarterly reimbursement rate adjustments for implantable and external cardiac monitoring devices across all service lines.",
      affectedProducts: 10,
      implementationDeadline: "2026-01-25"
    },
    {
      id: 6,
      title: "Spinal Implant Documentation Standards",
      category: "orthopedic",
      payer: "medicare",
      impact: "high",
      effectiveDate: "2026-03-15",
      description: "Enhanced documentation requirements for spinal fusion procedures and implant selection criteria with new reporting obligations.",
      affectedProducts: 14,
      implementationDeadline: "2026-03-01"
    }
  ];

  const filteredPolicies = policies?.filter(policy => {
    const matchesSearch = policy?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         policy?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || policy?.category === selectedCategory;
    const matchesPayer = selectedPayer === 'all' || policy?.payer === selectedPayer;
    const matchesImpact = selectedImpact === 'all' || policy?.impact === selectedImpact;
    
    return matchesSearch && matchesCategory && matchesPayer && matchesImpact;
  });

  const getImpactBadgeStyles = (impact) => {
    switch(impact) {
      case 'critical':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'medium':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'low':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <section className="bg-background py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Smart Policy Filtering
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Find relevant policies instantly with advanced filtering and search capabilities
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="lg:col-span-4">
              <Input
                type="search"
                placeholder="Search policies by title, description, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full"
              />
            </div>

            <Select
              label="Device Category"
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />

            <Select
              label="Payer Network"
              options={payerOptions}
              value={selectedPayer}
              onChange={setSelectedPayer}
            />

            <Select
              label="Impact Level"
              options={impactOptions}
              value={selectedImpact}
              onChange={setSelectedImpact}
            />

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedPayer('all');
                  setSelectedImpact('all');
                }}
                className="w-full px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                Reset Filters
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4 md:mb-6">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredPolicies?.length}</span> of <span className="font-semibold text-foreground">{policies?.length}</span> policies
            </p>
            <div className="flex items-center gap-2">
              <Icon name="Filter" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Active Filters: {
                [selectedCategory !== 'all', selectedPayer !== 'all', selectedImpact !== 'all', searchQuery !== '']?.filter(Boolean)?.length
              }</span>
            </div>
          </div>

          <div className="space-y-4 md:space-y-6">
            {filteredPolicies?.length === 0 ? (
              <div className="text-center py-12 md:py-16">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                  No Policies Found
                </h3>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your filters or search terms
                </p>
              </div>
            ) : (
              filteredPolicies?.map((policy) => (
                <div 
                  key={policy?.id}
                  className="bg-muted/50 border border-border rounded-lg p-4 md:p-6 hover:border-primary/20 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getImpactBadgeStyles(policy?.impact)}`}>
                          <Icon name="AlertCircle" size={12} />
                          {policy?.impact?.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-card border border-border rounded text-xs font-medium text-foreground">
                          <Icon name="Calendar" size={12} />
                          Effective: {policy?.effectiveDate}
                        </span>
                      </div>
                      <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                        {policy?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {policy?.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Tag" size={14} />
                          {categoryOptions?.find(c => c?.value === policy?.category)?.label}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Building2" size={14} />
                          {payerOptions?.find(p => p?.value === policy?.payer)?.label}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="Package" size={14} />
                          {policy?.affectedProducts} Products
                        </span>
                        <span className="flex items-center gap-1 text-warning">
                          <Icon name="Clock" size={14} />
                          Deadline: {policy?.implementationDeadline}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row lg:flex-col gap-2">
                      <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium whitespace-nowrap">
                        View Details
                      </button>
                      <button className="px-4 py-2 bg-card border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium whitespace-nowrap">
                        Export PDF
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeviceCategoryFiltering;